import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { IoCloseSharp } from "react-icons/io5";
import { BiSolidError } from "react-icons/bi";
import { RiDeleteBin6Fill } from "react-icons/ri";
import AdminPagination from "../../component/pagination/AdminPagination";
import SkeletonLoading from "../../component/SkeletonLoading";
import {
  useGetManualPaymentsQuery,
  useCompleteManualPaymentMutation,
  useDeleteManualPaymentMutation,
} from "../../store/api/manualPaymentSlice";
import "./ManualPaymentHistory.scss";
import { capitalizeString } from "../../utils/dateAndTimeConversion";

const toISODate = (d) => (d ? d.toISOString().split("T")[0] : undefined);

const initialPagination = { limit: 10, page: 1, total: "", totalPages: "" };

const statusClass = (s) => {
  if (!s) return "status unavailable";
  const lower = s.toLowerCase();
  if (lower === "completed") return "status bg-success text-white";
  if (lower === "failed") return "status bg-danger text-white";
  if (lower === "refunded") return "status bg-warning text-dark";
  if (lower === "pending") return "status bg-secondary text-white";
  return "status unavailable";
};

const ManualPaymentHistory = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.UserReducer);
  const loggedRole = user?.role;

  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState(initialPagination);
  const [activeStatus, setActiveStatus] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [filterView, setFilterView] = useState("all"); // "all" | "sent" | "received"
  const [retryingId, setRetryingId] = useState(null);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const queryParams = {
    page: pagination.page,
    limit: pagination.limit,
    ...(activeStatus && { status: activeStatus }),
    ...(fromDate && { from_date: toISODate(fromDate) }),
    ...(toDate && { to_date: toISODate(toDate) }),
    ...(filterView === "sent" && { payer_id: user?.id }),
    ...(filterView === "received" && { recipient_id: user?.id }),
  };

  const { data, isSuccess, isLoading, isFetching, refetch } =
    useGetManualPaymentsQuery(queryParams);

  const [completeManualPayment] = useCompleteManualPaymentMutation();
  const [deleteManualPayment, { isLoading: isDeleting }] =
    useDeleteManualPaymentMutation();

  useEffect(() => {
    if (isSuccess && data?.data) {
      setPayments(data.data.payments);
      setPagination((prev) => ({
        ...prev,
        total: data.data.pagination.total,
        page: data.data.pagination.page,
        limit: data.data.pagination.limit,
        totalPages: data.data.pagination.pages,
      }));
    }
  }, [data, isSuccess]);

  // Reset page on filter change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [activeStatus, fromDate, toDate, filterView]);

  const handleReset = () => {
    setActiveStatus("");
    setFromDate(null);
    setToDate(null);
    setFilterView("all");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Complete a pending payment — checks Omni first to avoid double-charging
  const handleComplete = async (paymentId) => {
    setRetryingId(paymentId);
    try {
      const res = await completeManualPayment(paymentId).unwrap();

      if (res.data?.already_paid) {
        toast.success(
          "Payment was already completed! Status has been updated.",
        );
      } else if (res.data?.needs_payment) {
        localStorage.setItem("manual_payment_id", paymentId);
        localStorage.setItem("manual_payment_txn", res.data.transaction_number);
        window.open(res.data.iframeUrl, "_blank");
        toast.info(
          "Payment page opened in a new tab. Complete the payment there.",
        );
      }
    } catch (err) {
      toast.error(err?.data?.message || "Failed to complete payment.");
    } finally {
      setRetryingId(null);
    }
  };

  // Check if this user is the payer (only payer can complete/delete)
  const isPayer = (item) => item.payer.id === user?.id;

  const handleDelete = async () => {
    try {
      await deleteManualPayment(deleteTargetId).unwrap();
      toast.success(t("payment_deleted"));
      setDeleteTargetId(null);
    } catch (err) {
      toast.error(err?.data?.message || t("failed_to_delete_payment"));
      setDeleteTargetId(null);
    }
  };

  return (
    <div className="mph-page">
      <div className="d-flex justify-content-between align-items-center flex-wrap w-100 mb-2">
        <div className="total">
          {t("total")}: {pagination?.total}
        </div>

        <div className="d-flex flex-wrap gap-2 ms-auto">
          <div className="d-flex flex-grow-1">
            {/* Status filter */}
            <select
              className="form-select"
              value={activeStatus}
              onChange={(e) => setActiveStatus(e.target.value)}
            >
              <option value="">{t("allStatus")}</option>
              <option value="completed">{t("completed")}</option>
              <option value="pending">{t("pending")}</option>
              <option value="failed">{t("failed")}</option>
            </select>
          </div>
          <div className="d-flex flex-grow-1">
            {/* Sent / Received filter */}
            <select
              className="form-select"
              value={filterView}
              onChange={(e) => setFilterView(e.target.value)}
            >
              <option value="all">{t("allPayments")}</option>
              <option value="sent">{t("sent")}</option>
              <option value="received">{t("received")}</option>
            </select>
          </div>
          <div className="d-flex flex-grow-1">
            <DatePicker
              selected={fromDate}
              onChange={(d) => setFromDate(d)}
              selectsStart
              startDate={fromDate}
              endDate={toDate}
              placeholderText={`${t("from")} ${t("date")}`}
              dateFormat="dd/MM/yyyy"
              className="form-control mph-date-input"
              isClearable
            />
          </div>
          <div className="d-flex flex-grow-1">
            <DatePicker
              selected={toDate}
              onChange={(d) => setToDate(d)}
              selectsEnd
              startDate={fromDate}
              endDate={toDate}
              minDate={fromDate}
              placeholderText={`${t("to")} ${t("date")}`}
              dateFormat="dd/MM/yyyy"
              className="form-control mph-date-input"
              isClearable
            />
          </div>

          <div className="d-flex gap-2 ms-auto">
            <button className="btn btn-primary addDriver" onClick={handleReset}>
              {t("btns.reset")}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th>{t("srNo")}</th>
              <th>{t("txnNo")}</th>
              <th>{t("sentBy")}</th>
              <th>{t("receivedBy")}</th>
              <th>{t("bankUsed")}</th>
              <th>{t("amount")}</th>
              <th>{t("description")}</th>
              <th>{t("status")}</th>
              <th>{t("date")}</th>
              <th>{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <SkeletonLoading MainLength={10} length={10} />
            ) : payments.length > 0 ? (
              payments.map((item, i) => (
                <tr key={item.id}>
                  <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>

                  {/* Transaction number */}
                  <td className="text-nowrap">
                    <span className="mph-txn-no">
                      {item.transaction_number}
                    </span>
                  </td>

                  {/* Sent By (Payer) */}
                  <td className="text-nowrap">
                    <div className="mph-user-cell">
                      <strong>{item.payer.name}</strong>
                      <span
                        className={`mph-role-badge mph-role-${item.payer.type}`}
                      >
                        {item.payer.type}
                      </span>
                      {item.payer.bank_name && (
                        <small className="mph-bank-hint">
                          🏦 {item.payer.bank_name}
                        </small>
                      )}
                    </div>
                  </td>

                  {/* Received By (Recipient) */}
                  <td className="text-nowrap">
                    <div className="mph-user-cell">
                      <strong>{item.recipient.name}</strong>
                      <span
                        className={`mph-role-badge mph-role-${item.recipient.type}`}
                      >
                        {item.recipient.type}
                      </span>
                      {/* If payment was routed through admin because recipient had no bank */}
                      {item.effective_bank?.routed_through_admin && (
                        <span className="mph-via-admin-badge">
                          {t("via")} {t("admin")}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Bank Used */}
                  <td className="text-nowrap">
                    {item.effective_bank ? (
                      <div className="mph-bank-cell">
                        <span>{item.effective_bank.bank_name || "—"}</span>
                        {item.effective_bank.routed_through_admin && (
                          <small className="mph-bank-owner">
                            ({capitalizeString(item.effective_bank.owner_role)})
                          </small>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>

                  {/* Amount */}
                  <td className="text-nowrap">
                    <strong>${item.amount}</strong>
                    <small className="ms-1 text-muted">{item.currency}</small>
                  </td>

                  {/* Description */}
                  <td>
                    <span className="mph-description" title={item.description}>
                      {item.description || "—"}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="text-nowrap">
                    <span className={statusClass(item.payment_status)}>
                      {item.payment_status &&
                        capitalizeString(item.payment_status)}
                    </span>
                  </td>

                  {/* Date */}
                  <td className="text-nowrap">
                    {item.created_at
                      ? new Date(item.created_at).toLocaleDateString()
                      : "—"}
                  </td>

                  {/* Action */}
                  <td className="text-nowrap">
                    <div className="d-flex gap-1 align-items-center">
                      {item.payment_status === "pending" && isPayer(item) && (
                        <button
                          className="mph-complete-btn"
                          onClick={() => handleComplete(item.id)}
                          disabled={retryingId === item.id}
                        >
                          {retryingId === item.id ? (
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          ) : (
                            t("complete")
                          )}
                        </button>
                      )}
                      {item.payment_status === "pending" &&
                        (isPayer(item) || loggedRole === "super_admin") && (
                          <Button
                            className="rounded-circle ms-1"
                            onClick={() => setDeleteTargetId(item.id)}
                            title={t("delete")}
                          >
                            <RiDeleteBin6Fill />
                          </Button>
                        )}
                      {item.payment_status !== "pending" && "—"}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="text-center">
                <td colSpan="10">{t("noManualPaymentsFound")}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
        onLimitChange={(limit) =>
          setPagination((prev) => ({ ...prev, limit, page: 1 }))
        }
      />

      {/* Delete Confirmation Modal */}
      <Modal show={!!deleteTargetId} centered>
        <Button className="closePopup" onClick={() => setDeleteTargetId(null)}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div className="text-center mb-3 errorIcon">
            <BiSolidError />
          </div>
          <div className="text-center mb-4 deleteHd">
            {t("confirmDeletePayment")}
          </div>
          <div className="text-center">
            <Button
              className="smallBtn cancel me-3"
              onClick={() => setDeleteTargetId(null)}
            >
              {t("btns.cancel")}
            </Button>
            <Button
              className="smallBtn auto"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                />
              ) : (
                t("btns.delete")
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ManualPaymentHistory;
